import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff, LockReset } from '@mui/icons-material';
import loginBanner from '../assets/login-banner.png';

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(true);

  // ✅ VALIDAR TOKEN AL CARGAR LA PÁGINA
  useEffect(() => {
    const urlToken = searchParams.get('token');
    
    console.log('🔍 Token recibido:', urlToken); // Para debug
    
    if (!urlToken) {
      console.error('❌ No hay token en la URL');
      setError('Token de recuperación inválido o expirado.');
      setIsValidating(false);
      return;
    }
    
    validateToken(urlToken);
  }, [searchParams]);

  const validateToken = async (urlToken: string) => {
    try {
      console.log('🔄 Validando token en backend...');
      
      const response = await fetch(`${API_URL}/api/validate-reset-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: urlToken })
      });
      
      const data = await response.json();
      
      console.log('📡 Respuesta del backend:', data);
      
      if (response.ok && data.valid) {
        console.log('✅ Token válido');
        setToken(urlToken);
        setIsValidating(false);
      } else {
        console.error('❌ Token inválido o expirado');
        setError('El enlace de recuperación ha expirado o es inválido.');
        setIsValidating(false);
      }
    } catch (err) {
      console.error('❌ Error al validar token:', err);
      setError('Error al validar el enlace de recuperación.');
      setIsValidating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validaciones
    if (!newPassword || !confirmPassword) {
      setError('Por favor complete ambos campos.');
      return;
    }

    if (newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('🔄 Enviando nueva contraseña...');
      
      const response = await fetch(`${API_URL}/api/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          token: token,
          newPassword: newPassword 
        })
      });

      const data = await response.json();
      
      console.log('📡 Respuesta del cambio:', data);

      if (response.ok) {
        console.log('✅ Contraseña actualizada');
        setSuccess('¡Contraseña actualizada exitosamente!');
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 2000);
      } else {
        throw new Error(data.message || 'Error al cambiar la contraseña');
      }
    } catch (err: any) {
      console.error('❌ Error:', err);
      setError('Error al procesar la solicitud. Intente nuevamente.');
      setIsSubmitting(false);
    }
  };

  // ✅ PANTALLA DE CARGA MIENTRAS VALIDA
  if (isValidating) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        backgroundColor: '#f0f2f5' 
      }}>
        <CircularProgress size={60} />
        <Typography sx={{ mt: 2 }} color="textSecondary">
          Validando enlace de recuperación...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f2f5' }}>
      <Container component="main" maxWidth="xs">
        <Paper elevation={6} sx={{ borderRadius: 4, overflow: 'hidden', display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Box sx={{ 
            height: 160, 
            width: '100%', 
            backgroundImage: `url(${loginBanner})`, 
            backgroundSize: 'cover', 
            backgroundPosition: 'center', 
            position: 'relative' 
          }}>
            <Box sx={{ 
              position: 'absolute', 
              bottom: 0, 
              left: 0, 
              width: '100%', 
              height: '60%', 
              background: 'linear-gradient(to top, rgba(255,255,255,1), transparent)' 
            }}/>
          </Box>
          
          <Box sx={{ p: 4, pt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
              <LockReset color="primary" fontSize="large" />
              <Typography component="h1" variant="h5" fontWeight="bold" color="primary">
                Restablecer Contraseña
              </Typography>
            </Box>

            {/* ✅ SI NO HAY TOKEN VÁLIDO, MOSTRAR ERROR Y BOTÓN */}
            {error && !token && (
              <Box>
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                <Button 
                  fullWidth 
                  variant="contained" 
                  onClick={() => navigate('/login')}
                  sx={{ borderRadius: 2 }}
                >
                  Volver al Login
                </Button>
              </Box>
            )}

            {/* ✅ SI HAY TOKEN VÁLIDO, MOSTRAR FORMULARIO */}
            {token && (
              <Box component="form" onSubmit={handleSubmit} noValidate>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="newPassword"
                  label="Nueva Contraseña"
                  type={showPassword ? 'text' : 'password'}
                  id="newPassword"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isSubmitting || !!success}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirmar Contraseña"
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isSubmitting || !!success}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{ mt: 3, mb: 2, borderRadius: 2, height: 48 }}
                  disabled={isSubmitting || !!success}
                  startIcon={isSubmitting ? <CircularProgress size={20} color="inherit"/> : <LockReset />}
                >
                  {isSubmitting ? 'Actualizando...' : success ? 'Redirigiendo...' : 'Actualizar Contraseña'}
                </Button>

                <Button
                  fullWidth
                  variant="text"
                  onClick={() => navigate('/login')}
                  disabled={isSubmitting}
                  sx={{ borderRadius: 2 }}
                >
                  Volver al Login
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ResetPasswordPage;